import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Chip,
  Alert,
  Tooltip,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  getTranslationsApi,
  updateTranslationsApi,
  resetTranslationsApi,
} from "../Apis/translations/translations";
import { useToast } from "../Hooks/useToast";

const Translations = () => {
  const { toastSuccess, toastError } = useToast();
  const [languages, setLanguages] = useState([]);
  const [sectionLabels, setSectionLabels] = useState({});
  const [data, setData] = useState({}); // { code: { key: value } }
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newValues, setNewValues] = useState({}); // { code: value }

  const load = useCallback(async () => {
    setIsLoading(true);
    const res = await getTranslationsApi();
    if (res.success) {
      setLanguages(res.languages || []);
      setSectionLabels(res.sectionLabels || {});
      setData(res.translations || {});
    } else {
      toastError(res.error || "Übersetzungen konnten nicht geladen werden.");
    }
    setIsLoading(false);
  }, [toastError]);

  useEffect(() => {
    load();
  }, [load]);

  // German is the reference/source; keys are derived from it.
  const allKeys = useMemo(() => Object.keys(data.de || {}), [data]);

  const nonGermanCodes = useMemo(
    () => languages.map((l) => l.code).filter((c) => c !== "de"),
    [languages]
  );

  // A key is "missing" for a language when German has a value but that
  // language's value is empty/undefined (it will silently fall back to German).
  const isMissingFor = useCallback(
    (code, key) => {
      const de = (data.de && data.de[key]) || "";
      if (!de) return false;
      const val = (data[code] && data[code][key]) || "";
      return val.trim() === "";
    },
    [data]
  );
  const keyHasAnyMissing = useCallback(
    (key) => nonGermanCodes.some((code) => isMissingFor(code, key)),
    [nonGermanCodes, isMissingFor]
  );

  // Count missing values per language (for the validation banner).
  const missingCounts = useMemo(() => {
    const counts = {};
    for (const code of nonGermanCodes) {
      counts[code] = allKeys.filter((key) => isMissingFor(code, key)).length;
    }
    return counts;
  }, [nonGermanCodes, allKeys, isMissingFor]);
  const totalMissing = useMemo(
    () => Object.values(missingCounts).reduce((a, b) => a + b, 0),
    [missingCounts]
  );

  // Group keys by section (prefix before the first ".").
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = {};
    for (const key of allKeys) {
      const section = key.split(".")[0];
      const germanVal = (data.de && data.de[key]) || "";
      if (
        q &&
        !key.toLowerCase().includes(q) &&
        !germanVal.toLowerCase().includes(q)
      ) {
        continue;
      }
      if (onlyMissing && !keyHasAnyMissing(key)) continue;
      if (!map[section]) map[section] = [];
      map[section].push(key);
    }
    return map;
  }, [allKeys, data, query, onlyMissing, keyHasAnyMissing]);

  const handleChange = (code, key, value) => {
    setData((prev) => ({
      ...prev,
      [code]: { ...(prev[code] || {}), [key]: value },
    }));
  };

  const handleAddKey = () => {
    const key = newKey.trim();
    if (!key) {
      toastError("Bitte einen Schlüssel eingeben (z. B. booking.confirm).");
      return;
    }
    if (!/^[a-zA-Z0-9_]+(\.[a-zA-Z0-9_]+)+$/.test(key)) {
      toastError('Schlüssel im Format "bereich.name" verwenden, z. B. booking.confirm.');
      return;
    }
    if (allKeys.includes(key)) {
      toastError("Dieser Schlüssel existiert bereits.");
      return;
    }
    const germanVal = (newValues.de || "").trim();
    if (!germanVal) {
      toastError("Der deutsche Text (Original) ist erforderlich.");
      return;
    }
    setData((prev) => {
      const next = { ...prev };
      for (const l of languages) {
        const code = l.code;
        next[code] = { ...(prev[code] || {}), [key]: (newValues[code] || "").trim() };
      }
      return next;
    });
    setNewKey("");
    setNewValues({});
    toastSuccess('Schlüssel hinzugefügt. Nicht vergessen: "Speichern".');
  };

  const handleSave = async () => {
    setIsSaving(true);
    const res = await updateTranslationsApi(data);
    if (res.success) {
      setData(res.translations || data);
      toastSuccess("Übersetzungen gespeichert.");
    } else {
      toastError(res.error || "Speichern fehlgeschlagen.");
    }
    setIsSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm("Alle Übersetzungen auf die Standardwerte zurücksetzen?")) return;
    setIsSaving(true);
    const res = await resetTranslationsApi();
    if (res.success) {
      setData(res.translations || {});
      toastSuccess("Auf Standardwerte zurückgesetzt.");
    } else {
      toastError(res.error || "Zurücksetzen fehlgeschlagen.");
    }
    setIsSaving(false);
  };

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" gap={2} p={4}>
        <CircularProgress size={22} />
        <Typography color="#334155">Lade Übersetzungen...</Typography>
      </Box>
    );
  }

  const nonGermanLangs = languages.filter((l) => l.code !== "de");

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
        <Box
          sx={{
            width: 38, height: 38, borderRadius: 2, display: "flex",
            alignItems: "center", justifyContent: "center",
            background: "#dbeafe", color: "#1d4ed8",
          }}
        >
          <TranslateRoundedIcon fontSize="small" />
        </Box>
        <Typography variant="h5" fontWeight={800} color="#0f172a">
          Übersetzungen
        </Typography>
      </Stack>
      <Typography variant="body2" color="#475569" mb={2}>
        Bearbeiten Sie die Texte der Kundenseite. Deutsch ist die Originalsprache.
        Leere Felder verwenden automatisch den deutschen Text. (E-Mails werden nicht übersetzt.)
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        mb={2}
        sx={{ position: "sticky", top: 0, zIndex: 2, background: "#fff", py: 1 }}
      >
        <TextField
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Text oder Schlüssel suchen..."
          sx={{ minWidth: { sm: 320 }, background: "#fff" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleReset}
            disabled={isSaving}
            sx={{ textTransform: "none", fontWeight: 600, borderColor: "#cbd5e1", color: "#334155" }}
          >
            Zurücksetzen
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: "none", fontWeight: 700, background: "#1d4ed8" }}
          >
            {isSaving ? "Speichern..." : "Speichern"}
          </Button>
        </Stack>
      </Stack>

      {/* Missing-value validation banner */}
      {totalMissing > 0 ? (
        <Alert
          severity="warning"
          icon={<WarningAmberRoundedIcon fontSize="small" />}
          sx={{ mb: 2, border: "1px solid #fde68a", background: "#fffbeb" }}
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() => setOnlyMissing((v) => !v)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {onlyMissing ? "Alle anzeigen" : "Nur fehlende anzeigen"}
            </Button>
          }
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Typography variant="body2" fontWeight={700} color="#92400e">
              {totalMissing} fehlende Übersetzung(en):
            </Typography>
            {nonGermanLangs.map((l) =>
              missingCounts[l.code] > 0 ? (
                <Chip
                  key={l.code}
                  size="small"
                  label={`${l.flag} ${l.label}: ${missingCounts[l.code]}`}
                  sx={{ height: 22, fontWeight: 700, background: "#fde68a", color: "#92400e" }}
                />
              ) : null
            )}
          </Stack>
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 2, border: "1px solid #bbf7d0", background: "#f0fdf4" }}>
          Alle Sprachen sind vollständig übersetzt.
        </Alert>
      )}

      {/* Add a new translation key */}
      <Accordion
        disableGutters
        sx={{ mb: 2, borderRadius: 2, border: "1px solid #bfdbfe", background: "#eff6ff", "&:before": { display: "none" } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AddRoundedIcon fontSize="small" sx={{ color: "#1d4ed8" }} />
            <Typography fontWeight={800} color="#1e3a8a">
              Neuen Text hinzufügen
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.5}>
            <TextField
              size="small"
              fullWidth
              label="Schlüssel (z. B. booking.confirm)"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="bereich.name"
              InputProps={{ style: { fontFamily: "monospace" } }}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                gap: 1.5,
              }}
            >
              <TextField
                label="🇩🇪 Deutsch (Original) *"
                size="small"
                fullWidth
                value={newValues.de || ""}
                onChange={(e) => setNewValues((p) => ({ ...p, de: e.target.value }))}
                InputProps={{ style: { background: "#f8fafc" } }}
              />
              {nonGermanLangs.map((l) => {
                const isRtl = l.code === "ar";
                return (
                  <TextField
                    key={l.code}
                    label={`${l.flag} ${l.label}`}
                    size="small"
                    fullWidth
                    value={newValues[l.code] || ""}
                    onChange={(e) => setNewValues((p) => ({ ...p, [l.code]: e.target.value }))}
                    inputProps={{ dir: isRtl ? "rtl" : "ltr" }}
                  />
                );
              })}
            </Box>
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={handleAddKey}
                sx={{ textTransform: "none", fontWeight: 700, background: "#1d4ed8" }}
              >
                Hinzufügen
              </Button>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {Object.keys(groups).length === 0 && (
        <Typography color="#64748b" sx={{ py: 3 }}>Keine Treffer.</Typography>
      )}

      {Object.keys(groups).map((section) => (
        <Accordion key={section} defaultExpanded disableGutters sx={{ mb: 1, borderRadius: 2, border: "1px solid #e2e8f0", "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography fontWeight={800} color="#1e293b">
                {sectionLabels[section] || section}
              </Typography>
              <Chip size="small" label={groups[section].length} sx={{ height: 20, background: "#e2e8f0" }} />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {groups[section].map((key) => (
                <Box key={key} sx={{ borderBottom: "1px dashed #e5e7eb", pb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: "#94a3b8", fontFamily: "monospace" }}>
                    {key}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                      gap: 1.5,
                      mt: 0.5,
                    }}
                  >
                    {/* German (reference, read-only styling but editable) */}
                    <TextField
                      label="🇩🇪 Deutsch (Original)"
                      size="small"
                      fullWidth
                      value={(data.de && data.de[key]) || ""}
                      onChange={(e) => handleChange("de", key, e.target.value)}
                      InputProps={{ style: { background: "#f8fafc" } }}
                    />
                    {nonGermanLangs.map((l) => {
                      const isRtl = l.code === "ar";
                      const missing = isMissingFor(l.code, key);
                      return (
                        <TextField
                          key={l.code}
                          label={`${l.flag} ${l.label}${missing ? " — fehlt" : ""}`}
                          size="small"
                          fullWidth
                          error={missing}
                          helperText={missing ? "Leer → verwendet den deutschen Text" : " "}
                          value={(data[l.code] && data[l.code][key]) || ""}
                          onChange={(e) => handleChange(l.code, key, e.target.value)}
                          placeholder={(data.de && data.de[key]) || ""}
                          inputProps={{ dir: isRtl ? "rtl" : "ltr" }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}

      <Divider sx={{ my: 2 }} />
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          sx={{ textTransform: "none", fontWeight: 700, background: "#1d4ed8" }}
        >
          {isSaving ? "Speichern..." : "Speichern"}
        </Button>
      </Stack>
    </Box>
  );
};

export default Translations;
